/**
 * DownloadOption collection
 * @class
 */
import {getPreference} from '@netx/core-user-next/utils/getPreference';
import Base from '@netx/core/lib/netx-collection';
import Model from '../models/downloadOption';
import {types as downloadOptionTypes} from '../properties/downloadOptions';

export default class DownloadOptions extends Base {
	/** @override */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;
		super(models, opts);
	}

	/**
	 * @method
	 * @param {string} type Download options type
	 */
	filterTypes(type) {
		if (type != null) {
			// filter us by the given type.
			return this.where({type: type});
		}

		// If no argument, return an object with all our models split out by type.
		const filtered = {
			originalOption: this.where({type: downloadOptionTypes.original}) || [],
			proxyOptions: this.where({type: downloadOptionTypes.proxy}) || [],
			viewOptions: this.where({type: downloadOptionTypes.assetView}) || [],
			processOptions:
				this.where({type: downloadOptionTypes.presetProcess}) || [],
			metadataOptions: this.where({type: downloadOptionTypes.metadata}) || [],
			contactSheetOptions:
				this.where({type: downloadOptionTypes.contactSheet}) || [],
		};
		// These three are sometimes lumped together:
		filtered.vppOptions = filtered.viewOptions
			.concat(filtered.proxyOptions)
			.concat(filtered.processOptions);
		return filtered;
	}

	/**
	 * @method
	 * @param {string} type Download options type
	 * @return {module:share/models/downloadOption}
	 */
	getOption(type, name) {
		const opts = {};

		if (type != null) {
			opts.type = type;
		}

		if (name != null) {
			opts.name = name;
		}

		return this.findWhere(opts);
	}

	/**
	 * @method
	 * @return {boolean} Has manual repurpose options
	 */
	hasManualRepurposeOptions() {
		const manualRepurpose = this.getOption('manualRepurpose');
		return manualRepurpose && manualRepurpose.get('options').length > 0;
	}

	/**
	 * @method
	 * @param {string} type Download options type
	 * @return {boolean} Has option
	 */
	hasOption(type, name) {
		return this.getOption(type, name) != null;
	}

	/**
	 * Takes the selected download options and filters this collection to only contain selected download options
	 * @method
	 * @param  {object} selectedDownloadOptions Form object containing user selected download options
	 */
	parseSelectedDownloadOptions(selectedDownloadOptions) {
		// reduce the option set to just the selected items
		// continue to next step in workflow: check justification

		// DAM-12078 - set organizeContentsBy on all options but pdf and metadata

		const selectedDownloadOptionModels = [];

		let requiresJustification = false;
		let requiresApproval = false;

		if (selectedDownloadOptions.original) {
			const originalOption = this.getOption('original');
			if (originalOption) {
				originalOption.set({
					organizeContentsBy: selectedDownloadOptions.organizeContentsBy,
				});
				selectedDownloadOptionModels.push(originalOption);
			}
		}

		// Manual repurpose
		// If it has options, we use it.
		const manualRepurpose = this.getOption('manualRepurpose');
		const manualRepurposeOpts =
			(manualRepurpose && manualRepurpose.get('options')) || [];
		if (manualRepurposeOpts.length) {
			manualRepurpose.set({
				organizeContentsBy: selectedDownloadOptions.organizeContentsBy,
			});
			selectedDownloadOptionModels.push(manualRepurpose);
		}

		// Loop over sleected proxies
		let proxyDownloadOption;
		_.each(selectedDownloadOptions.proxies, (proxyName) => {
			proxyDownloadOption = this.getOption('proxy', proxyName);
			if (proxyDownloadOption) {
				proxyDownloadOption.set({
					organizeContentsBy: selectedDownloadOptions.organizeContentsBy,
				});
				selectedDownloadOptionModels.push(proxyDownloadOption);
			}
		});

		// Loop over selected views
		let viewDownloadOption;
		_.each(selectedDownloadOptions.views, (viewName) => {
			viewDownloadOption = this.getOption('assetView', viewName);
			if (viewDownloadOption) {
				viewDownloadOption.set({
					organizeContentsBy: selectedDownloadOptions.organizeContentsBy,
				});
				selectedDownloadOptionModels.push(
					this.getOption('assetView', viewName),
				);
			}
		});

		// Loop over selected preset processes
		_.each(selectedDownloadOptions.processes, (processId) => {
			// If it is a true preset process (user defined) it has a numeric id
			// otherwise it is one of those 'fancy' preset processes that come from an XML file and use a unique name rather than a numeric id
			const presetProcess = !_.isNumeric(processId)
				? // Assume name
				  this.getOption('presetProcess', processId)
				: // Assume id
				  this.findWhere({type: 'presetProcess', id: parseInt(processId)});

			if (!presetProcess) return;

			presetProcess.set({
				organizeContentsBy: selectedDownloadOptions.organizeContentsBy,
			});

			selectedDownloadOptionModels.push(presetProcess);
		});

		// PDF (contact sheet)
		if (selectedDownloadOptions.contactSheet.selected) {
			let sheetOption = this.getOption('contactSheet');
			if (sheetOption) {
				sheetOption.set(
					_.pick(selectedDownloadOptions.contactSheet, 'name', 'attributeIds'),
				);
				// Get rid of our client attributes - do not want to risk borking the prepareDownload call
				_.each(sheetOption.clientAttrs, (attr) => {
					sheetOption.unset(attr);
				});
				// I am guessing the server does not want a null title - so at least respect the other options
				const toPick = [
					'displayLabels',
					'highRes',
					'pdfExportPreset',
					'templateName',
				];
				if (selectedDownloadOptions.contactSheet.title) {
					toPick.push('title');
				}
				sheetOption.addOptions(
					_.pick.apply(
						_,
						[selectedDownloadOptions.contactSheet].concat(toPick),
					),
				);

				// DAM-8650: ship our default asset sort prefs to the PDF creator.
				// (Maybe someday this will be part of the PDF form, and we'll do this differently.)
				sheetOption.addOptions({
					assetListSortField: getPreference('asset.list.sort.field'),
					assetListSortOrder: getPreference('asset.list.sort.order'),
				});

				selectedDownloadOptionModels.push(sheetOption);
			}
		}

		// Attribute data
		if (selectedDownloadOptions.metadata.selected) {
			// Get a clone of our attribute download option model (not entirely sure why we clone since these things get wiped out on each download prompt)
			// I suppose the clone could be a safeguard for when the prepareDownload call fails, that way we haven't modified the model that the downloadOptionsView
			// is using (since it will still be open on an error)
			let metadataOption = this.getOption('metadata');
			if (metadataOption) {
				metadataOption = metadataOption.clone();
				const setId = selectedDownloadOptions.metadata.setId;
				// If metadata manager is not enabled then we have no attributeSets, only custom
				// if (setId == 'custom' || setId == '') {
				// 	// User specified a custom set.  We received the IDs from the selectedDownloadOptions.
				// 	metadataOption.set(
				// 		_.pick(selectedDownloadOptions.metadata, 'attributeIds'),
				// 	);
				// } else {
				// 	// User specified an existing set.  Get those IDs.
				// 	metadataOption.set(
				// 		'attributeIds',
				// 		Netx.asset.attributeSets.get(setId).get('attributeIds'),
				// 	);
				// }
				metadataOption.set(
					_.pick(selectedDownloadOptions.metadata, 'attributeIds'),
				);
				selectedDownloadOptionModels.push(metadataOption);
			}
		}

		// Historical attribute data
		if (selectedDownloadOptions.historical) {
			const historicalOption = this.getOption('historical');
			if (historicalOption) {
				selectedDownloadOptionModels.push(historicalOption);
			}
		}

		// Set this collection with the selected download options
		this.reset(selectedDownloadOptionModels);
	}

	/**
	 * Takes the download options that have already been selected and set the selected attribute as true.
	 * This is used so the correct download options are selected when editing a token
	 * @method
	 * @param {Netx.Collection.DownloadOptions} selectedDownloadOptions The collection of downloadOptions that are selected
	 */
	setUserSelectedDownloadOptions(selectedDownloadOptions) {
		// Set original download selected to false. If it's undefined the template will default to using the user pref for original which we do not want when editing
		// If it is selected, it it will be set correctly below
		const originalOption = this.findWhere({type: 'original'});
		originalOption && originalOption.set({selected: false});

		_.each(selectedDownloadOptions, (option, idx, all) => {
			let opt;
			switch (option.type) {
				case 'original':
					opt = this.findWhere({type: 'original'});
					if (opt) {
						opt.set({selected: true});
					}
					break;
				case 'proxy':
					opt = this.findWhere({name: option.name});
					if (opt) {
						opt.set({selected: true});
					}
					break;
				case 'presetProcess':
					opt = this.findWhere({name: option.name});
					if (opt) {
						opt.set(option).set({selected: true});
					}
					break;
				case 'contactSheet':
				case 'historical':
				case 'metadata':
					opt = this.findWhere({type: option.type});
					if (opt) {
						opt.set(option);
					}
					break;
			}
			// todo: what about views?
		});
	}

	/** @type {string} */
	static get __name__() {
		return 'DownloadOptions';
	}
}
